<!--
  Created 26.06.2023
  Modal for editing content version settings.
-->
<template>
  <div
    class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-gray-300
    bg-white opacity-90 shadow-sm hover:opacity-100"
    :class="{'!cursor-not-allowed hover:!opacity-90':!canEdit}">
    <CogIcon
      class="h-7 w-7 rounded-md text-gray-400 drop-shadow-sm hover:text-gray-500"
      :class="{'hover:!text-gray-400':!canEdit}"
      aria-hidden="true"
      @click="showModal=true" />
  </div>
  <SimpleModalSmall
    class="z-50"
    :show="showModal"
    heading="Configure the currently selected content version"
    text=""
    button-text="Close"
    :overflow-hidden="false"
    @update:show="(value) => showModal = value">
    <div
      v-if="!canEdit"
      class="text-sm italic text-gray-600">
      <p>This content version belongs to your team or a team member, and you do not have rights to configure this content version.</p>
    </div>
    <div v-else>
      <p class="text-sm text-gray-500">
        Here you can edit the current content version title<span v-if="canDelete">, set it as default or delete it entirely</span>.
      </p>
      <TextEditableTextarea
        v-model="modelValue.title"
        :tabindex="2"
        label="Text version title (max 40 characters)"
        text-area-class="!border-solid border-gray-300 p-2"
        class="my-4"
        :text-area-max-length="40"
        :tab-rows="1"
        :auto-grow="false"
        @save="(value) => { $emit('update:title', value); showModal = false; }" />
      <button
        v-if="modelValue.owner_type === 'App\\Models\\User'"
        tabindex="3"
        type="submit"
        class="mt-6 flex w-full justify-center rounded-md bg-blue-600 px-3 py-2
          text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:w-auto"
        @click="$emit('update:default', !modelValue.standard); showModal = false;">
        {{ modelValue.standard ? 'Remove' : 'Set' }} this content version as your default
      </button>
      <div
        v-if="canDelete && modelValue.owner_type !== 'App\\Models\\Team'"
        class="mt-4 flex flex-row items-center space-x-4">
        <button
          tabindex="4"
          type="submit"
          title="This is not reversible!"
          class="flex w-full justify-center rounded-md bg-red-600 px-3 py-2
            text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:w-auto"
          @click="$emit('delete:version', modelValue.id); showModal = false;">
          Delete content version
        </button>
        <small class="block">NB: Deleting content versions are irreversible!</small>
      </div>
      <div
        v-if="entityType && canDelete"
        class="mt-8 flex flex-row items-center space-x-4">
        <button
          tabindex="6"
          type="submit"
          :disabled="!checked"
          class="flex w-full shrink-0 justify-center rounded-md bg-red-600 px-3 py-2
            text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed
            disabled:bg-gray-400 disabled:opacity-50 sm:w-auto"
          @click="$emit('delete:entity', { placeId: entity.place_id, entityId: entity.id, entityTypeSlug, entityName: entity.label }); showModal = false;">
          Delete entity
        </button>
        <div class="flex flex-row items-center text-xs">
          <Checkbox
            id="confirm_delete_entity"
            v-model="checked"
            tabindex="5"
            class=""
            label="I understand"
            name="delete_entity"
            :checked="false" />
          <Label
            for="confirm_delete_entity"
            class="ml-2 text-xs text-gray-500">I understand that this action is irreversible and that it includes all content versions.</Label>
        </div>
      </div>
    </div>
  </SimpleModalSmall>
</template>

<script setup>
import {
  computed, ref,
} from 'vue';
import { CogIcon } from '@heroicons/vue/24/solid';
import SimpleModalSmall from '@/Components/Modals/SimpleModalSmall.vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import Checkbox from '@/Jetstream/Checkbox.vue';
import Label from '@/Jetstream/Label.vue';

const emit = defineEmits(['update:title', 'update:default', 'delete:version', 'delete:entity']);

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  entity: {
    type: Object,
    required: false,
    default: null,
  },
  entityType: {
    type: String,
    required: false,
    default: null,
  },
  entityTypeSlug: {
    type: String,
    required: false,
    default: null,
  },
  canEdit: {
    type: Boolean,
    required: false,
    default: false,
  },
  canDelete: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const showModal = ref(false);
const checked = ref(false);
const modelValue = computed(() => props.modelValue);

</script>
